import { useStorage } from '@vueuse/core';
import { defineStore } from 'pinia';
import { computed, reactive, ref } from 'vue';

/**
 * The types from which to start a quote.
 * This determines whether to set the crypto or fiat amount in the quote request.
 */
export const enum QuoteFrom {
    FIAT = 'fiat',
    CRYPTO = 'crypto'
}

export const enum UnconfirmableReasons {
    CURRENCY_LIMIT = 'currencyLimitExceeded',
    PAYMENT_METHOD_LIMIT = 'paymentMethodLimitExceeded',
    PAYMENT_METHOD_DISABLED = 'paymentMethodDisabled',
    USER_LIMIT = 'userLimitExceeded',
    USER_REGISTRATION = 'userRegistrationNotCompleted'
}

interface QuoteRequest {
    amount: number;
    from: QuoteFrom;
}

interface QuoteResponse {
    cryptoAmount: number;
    currencyLimitExceeded: boolean;
    currencyPair: string;
    expiryDate: string;
    fiatAmount: number;
    quote: string;
    quoteUnconfirmableReasons: UnconfirmableReasons[];
    userLimitExceeded: boolean;
    partnerFee: number;
    btcdirectFee: number;
    btcdirectFeePercentage: number;
    partnerFeePercentage: number;
}

export interface CoinToFiatQuoteResponse extends QuoteResponse {
    bankAccountDisabled: boolean;
    fiatNetworkFee: number
}

export interface FiatToCoinQuoteResponse extends QuoteResponse {
    networkFeeCost: number;
    paymentMethod: string;
    paymentMethodCost: number;
    paymentMethodLimitExceeded: boolean;
    totalCost: number;
}

export const useQuoteStore = defineStore('quote', () => {
    const hasAttemptedDirectConfirm = ref<boolean>(false);

    const request = useStorage('quote-request', {
        fiat: null,
        crypto: null,
        from: QuoteFrom.FIAT
    });

    const responses: { coinToFiat: CoinToFiatQuoteResponse; fiatToCoin: FiatToCoinQuoteResponse } = reactive({
        coinToFiat: null,
        fiatToCoin: null
    });

    function setFiatAmount(amount: number): void {
        request.value.fiat = amount;
        request.value.crypto = null;
        request.value.from = QuoteFrom.FIAT;
    }

    function setCryptoAmount(amount: number): void {
        request.value.fiat = null;
        request.value.crypto = amount;
        request.value.from = QuoteFrom.CRYPTO;
    }

    function setCoinToFiatQuote(c2f: CoinToFiatQuoteResponse): void {
        responses.coinToFiat = c2f;
    }

    function setFiatToCoinQuote(f2c: FiatToCoinQuoteResponse): void {
        responses.fiatToCoin = f2c;
    }

    function registerAttemptedDirectConfirm(): void {
        hasAttemptedDirectConfirm.value = true;
    }

    return {
        request: computed<QuoteRequest>(() => ({
            from: request.value.from,
            amount: request.value.from === QuoteFrom.CRYPTO ? request.value.crypto : request.value.fiat
        })),
        coinToFiat: computed<CoinToFiatQuoteResponse>(() => responses.coinToFiat),
        fiatToCoin: computed<FiatToCoinQuoteResponse>(() => responses.fiatToCoin),
        setFiatAmount,
        setCryptoAmount,
        setCoinToFiatQuote,
        setFiatToCoinQuote,
        hasAttemptedDirectConfirm,
        registerAttemptedDirectConfirm
    };
});
